import * as z from 'zod';
import { EditorDocumentSchema } from './editor.ts';

export const Id = z.uuid();
const Timestamp = z.string();

export const Role = z.enum(['owner', 'editor', 'viewer']);
export type Role = z.infer<typeof Role>;
export const ItemKind = z.enum(['note', 'file', 'extracted_text', 'transcript', 'site']);
export type ItemKind = z.infer<typeof ItemKind>;
/** Why a recording was made: a voice note (a cleaned transcript follows) or a meeting (meeting notes follow). */
export const CaptureKind = z.enum(['voice_note', 'meeting']);
export type CaptureKind = z.infer<typeof CaptureKind>;
export const Lifecycle = z.enum(['active', 'trashed', 'purging', 'purged']);
export type Lifecycle = z.infer<typeof Lifecycle>;
export const FieldOrigin = z.enum(['unset', 'system', 'manual']);
export type FieldOrigin = z.infer<typeof FieldOrigin>;
export const PlacementOrigin = z.enum(['default', 'explicit', 'system']);
export const StorageState = z.enum(['pending', 'uploading', 'verifying', 'available', 'quarantined', 'failed']);
export type StorageState = z.infer<typeof StorageState>;
export const OperationKind = z.enum(['extract_text', 'enrich_metadata', 'transform', 'purge', 'file_item', 'transcribe', 'generate_site']);
export type OperationKind = z.infer<typeof OperationKind>;
export const OperationStatus = z.enum(['queued', 'running', 'succeeded', 'partial', 'unsupported', 'failed', 'cancelled']);
export type OperationStatus = z.infer<typeof OperationStatus>;
export const ActorKind = z.enum(['user', 'agent', 'system']);
export type ActorKind = z.infer<typeof ActorKind>;
export const TagOrigin = z.enum(['manual', 'system']);

export const ActorRef = z.object({
  kind: ActorKind,
  userId: Id.nullable(),
  connectionId: Id.nullable(),
  displayName: z.string().nullable(),
});
export type ActorRef = z.infer<typeof ActorRef>;

export const Folder = z.object({
  id: Id,
  parentId: Id.nullable(),
  name: z.string(),
  systemRole: z.enum(['inbox']).nullable(),
  origin: z.enum(['manual', 'system']),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type Folder = z.infer<typeof Folder>;

/** One processing stage of an item (an operation); UI readiness is a projection over these. */
export const Stage = z.object({
  operationId: Id,
  kind: OperationKind,
  status: OperationStatus,
  trigger: z.enum(['automatic', 'requested']),
  attempts: z.number().int(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  /** Plain-language notes on what was not covered (e.g. scanned pages) or found. */
  notes: z.array(z.string()),
  /** The item this step produced (extracted text, a written note), when it produced one. */
  outputItemId: Id.nullable(),
  updatedAt: Timestamp,
});
export type Stage = z.infer<typeof Stage>;

export const ItemSummary = z.object({
  id: Id,
  kind: ItemKind,
  title: z.string(),
  /** title, or a readable fallback such as "Untitled note" when the title is empty */
  displayTitle: z.string(),
  description: z.string().nullable(),
  folderId: Id,
  lifecycle: Lifecycle,
  tags: z.array(z.string()),
  mimeType: z.string().nullable(),
  sizeBytes: z.number().nullable(),
  storageState: StorageState.nullable(),
  /** For recordings: made as a voice note or a meeting; null otherwise. */
  capture: CaptureKind.nullable(),
  /** For recordings, once transcribed: length, and whether there are pictures as well as sound. */
  media: z.object({ durationMs: z.number().int(), video: z.boolean() }).nullable(),
  /** Direct sources this item was generated from (e.g. extracted text -> file). */
  sourceItemIds: z.array(Id),
  createdAt: Timestamp,
  updatedAt: Timestamp,
  trashedAt: Timestamp.nullable(),
});
export type ItemSummary = z.infer<typeof ItemSummary>;

export const Item = ItemSummary.extend({
  titleOrigin: FieldOrigin,
  descriptionOrigin: FieldOrigin,
  placementOrigin: PlacementOrigin,
  /** Opaque save token: pass as baseRevisionId when replacing or appending to a note body. */
  currentRevisionId: Id.nullable(),
  tagDetails: z.array(z.object({ tag: z.string(), origin: TagOrigin })),
  createdBy: ActorRef,
  stages: z.array(Stage),
  /** Actions the caller may take on this item right now. */
  permittedActions: z.array(z.string()),
});
export type Item = z.infer<typeof Item>;

export const ItemPage = z.object({ items: z.array(ItemSummary), nextCursor: z.string().nullable() });
export type ItemPage = z.infer<typeof ItemPage>;

export const NoteContent = z.object({
  itemId: Id,
  revisionId: Id,
  format: z.literal('tiptap_json_v1'),
  document: EditorDocumentSchema,
  markdown: z.string(),
  plainText: z.string(),
});
export type NoteContent = z.infer<typeof NoteContent>;

/** Bounded text read with continuation (extracted text, note text, supported file text). */
export const TextSection = z.object({ label: z.string(), start: z.number().int(), end: z.number().int() });

export const TextSlice = z.object({
  itemId: Id,
  revisionId: Id,
  text: z.string(),
  offset: z.number().int(),
  nextOffset: z.number().int().nullable(),
  totalChars: z.number().int(),
  /** Where pages, slides or sheets start and end in the full text (character offsets). */
  sectionKind: z.enum(['page', 'slide', 'sheet', 'time']).nullable(),
  sections: z.array(TextSection),
  /**
   * For a transcript as transcribed: when each stretch of this slice was heard (ms from the start of the
   * recording; `start`/`end` are character offsets in the text). Empty once the text was edited.
   */
  timings: z.array(z.object({ start: z.number().int(), end: z.number().int(), startMs: z.number().int(), endMs: z.number().int(), speaker: z.string().nullable() })),
});
export type TextSlice = z.infer<typeof TextSlice>;

export const SaveOutcome = z.discriminatedUnion('status', [
  z.object({ status: z.literal('saved'), itemId: Id, revisionId: Id }),
  z.object({ status: z.literal('conflict'), itemId: Id, currentRevisionId: Id }),
]);
export type SaveOutcome = z.infer<typeof SaveOutcome>;

export const Operation = z.object({
  id: Id,
  kind: OperationKind,
  status: OperationStatus,
  trigger: z.enum(['automatic', 'requested']),
  targetItemId: Id,
  attempts: z.number().int(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  result: z.record(z.string(), z.unknown()),
  /** For writing: what was asked for (summary, brief, report, notes, custom, clean_transcript, meeting_notes); null otherwise. */
  purpose: z.string().nullable(),
  requestedBy: ActorRef,
  createdAt: Timestamp,
  updatedAt: Timestamp,
  finishedAt: Timestamp.nullable(),
});
export type Operation = z.infer<typeof Operation>;

export const RelatedNode = z.object({
  itemId: Id,
  kind: ItemKind,
  /** null when the item is no longer readable (purged); trashed items keep their title */
  title: z.string().nullable(),
  lifecycle: Lifecycle,
  available: z.boolean(),
  relation: z.string(),
  depth: z.number().int(),
  viaItemId: Id,
  sourceRevisionId: Id,
  resultRevisionId: Id,
});
export type RelatedNode = z.infer<typeof RelatedNode>;

export const Related = z.object({
  itemId: Id,
  direction: z.enum(['sources', 'derivatives']),
  nodes: z.array(RelatedNode),
  truncated: z.boolean(),
});
export type Related = z.infer<typeof Related>;

export const SearchHit = z.object({
  item: ItemSummary,
  snippet: z.string(),
  revisionId: Id.nullable(),
  score: z.number(),
  /** Found by the words themselves, by meaning, or both. */
  match: z.enum(['words', 'meaning', 'both']),
  /** Page, slide or sheet of the passage that matched by meaning, when known. */
  location: z.string().nullable(),
  /** That passage's exact place in the item's text (read it with read_text from `start`). */
  passage: z.object({ revisionId: Id, start: z.number().int(), end: z.number().int() }).nullable(),
  /** For text read from a file: the file. `title` is null once it is permanently deleted. */
  source: z.object({ itemId: Id, title: z.string().nullable(), available: z.boolean() }).nullable(),
});
export type SearchHit = z.infer<typeof SearchHit>;

export const SearchResult = z.object({
  hits: z.array(SearchHit),
  /** Search by meaning: on; on while the latest changes are still being added; or off (not set up). */
  meaning: z.enum(['on', 'catching_up', 'off']),
});
export type SearchResult = z.infer<typeof SearchResult>;

export const ActivityEvent = z.object({
  seq: z.number().int(),
  actor: ActorRef,
  action: z.string(),
  targetKind: z.enum(['item', 'folder', 'operation', 'member', 'connection', 'workspace', 'invitation', 'share']),
  targetId: Id.nullable(),
  details: z.record(z.string(), z.unknown()),
  createdAt: Timestamp,
});
export type ActivityEvent = z.infer<typeof ActivityEvent>;

export const Member = z.object({
  userId: Id,
  email: z.string().nullable(),
  displayName: z.string().nullable(),
  role: Role,
  status: z.enum(['active', 'removed']),
});
export type Member = z.infer<typeof Member>;

export const AgentConnection = z.object({
  id: Id,
  userId: Id,
  userDisplayName: z.string().nullable(),
  clientName: z.string(),
  authKind: z.enum(['oauth', 'token']),
  clientId: z.string().nullable(),
  tokenHint: z.string().nullable(),
  status: z.enum(['active', 'revoked']),
  createdAt: Timestamp,
  lastUsedAt: Timestamp.nullable(),
  revokedAt: Timestamp.nullable(),
});
export type AgentConnection = z.infer<typeof AgentConnection>;

export const WorkspaceInfo = z.object({
  id: Id,
  name: z.string(),
  role: Role,
  inboxFolderId: Id,
  actor: ActorRef,
  changeSeq: z.number().int(),
});
export type WorkspaceInfo = z.infer<typeof WorkspaceInfo>;

const Allowance = z.object({ used: z.number(), allowance: z.number() });

/**
 * How a workspace is doing, for its owners: stored files against the storage
 * allowance, today's use of paid services against their allowances (UTC
 * day), and background work waiting, running, failed or in progress.
 */
export const WorkspaceStatus = z.object({
  storage: z.object({ usedBytes: z.number().int(), allowanceBytes: z.number().int() }),
  usageToday: z.object({ aiRuns: Allowance, transcriptionMinutes: Allowance, readAloudCharacters: Allowance }),
  work: z.object({
    waiting: z.number().int(),
    running: z.number().int(),
    /** How long the oldest waiting step has waited, or null when nothing waits. */
    oldestWaitingSeconds: z.number().int().nullable(),
    /** Steps that failed in the last 24 hours, by kind. */
    failedLastDay: z.array(z.object({ kind: OperationKind, count: z.number().int() })),
    deletionsInProgress: z.number().int(),
    /** Items whose passages for search by meaning are not yet up to date. */
    waitingForSearch: z.number().int(),
  }),
  /** The new system only (G5): the workspace's own database against the 10 GB a workspace may hold, with a warning from 8 GB. */
  database: z.object({ usedBytes: z.number().int(), limitBytes: z.number().int(), warnAtBytes: z.number().int(), nearLimit: z.boolean() }).optional(),
});
export type WorkspaceStatus = z.infer<typeof WorkspaceStatus>;

export const Upload = z.object({
  uploadId: Id,
  itemId: Id,
  /**
   * PUT the raw bytes here before completing: the whole file in one request,
   * or (required above 100 MB when hosted) each part to `<uploadUrl>/parts/<n>`,
   * n from 1, every part `partSize` bytes except the last. GET it to see which
   * parts arrived, to resume.
   */
  uploadUrl: z.string(),
  expiresAt: Timestamp,
  maxBytes: z.number().int(),
  partSize: z.number().int().positive(),
  partCount: z.number().int().positive(),
});
export type Upload = z.infer<typeof Upload>;

/** What an upload has received so far, for resuming (read with the upload link). */
export const UploadStatus = z.object({
  uploadId: Id,
  sizeBytes: z.number().int(),
  partSize: z.number().int().positive(),
  partCount: z.number().int().positive(),
  receivedParts: z.array(z.number().int().positive()),
  state: z.enum(['open', 'received', 'finalized', 'failed', 'expired']),
  expiresAt: Timestamp,
});
export type UploadStatus = z.infer<typeof UploadStatus>;

export const DownloadLink = z.object({
  itemId: Id,
  url: z.string(),
  expiresAt: Timestamp,
  sha256: z.string(),
  sizeBytes: z.number().int(),
  mimeType: z.string(),
  filename: z.string(),
});
export type DownloadLink = z.infer<typeof DownloadLink>;

/** A short-lived link a browser can play (and seek in) a recording from. */
export const PlaybackLink = z.object({
  itemId: Id,
  url: z.string(),
  expiresAt: Timestamp,
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  /** Which player fits: sound only, or a video. */
  media: z.enum(['audio', 'video']),
});
export type PlaybackLink = z.infer<typeof PlaybackLink>;

/** A key combination such as "Ctrl+Shift+Space" or "Alt+D" (at least one modifier), or a function key alone ("F8"). */
export const Shortcut = z
  .string()
  .regex(/^(?:(?:(?:Ctrl|Alt|Shift|Meta)\+){1,3}(?:[A-Z0-9]|Space|F(?:[1-9]|1[0-2]))|F(?:[1-9]|1[0-2]))$/, 'Use a key with Ctrl, Alt, Shift or Meta, e.g. "Ctrl+Shift+Space", or a function key such as "F8".');
/** ISO 639-1 language code, e.g. "en", "hi", "gu". */
export const LanguageCode = z.string().regex(/^[a-z]{2}$/, 'Use a two-letter language code such as "en", "hi" or "gu".');

/** Personal settings shared by every Mystiq app (web now; desktop and iPhone later). */
export const Preferences = z.object({
  /** Languages the person speaks, listened for in dictation and in their recordings; empty means any language. */
  languages: z.array(LanguageCode).max(5),
  dictation: z.object({
    /** Starts and stops dictation while a Mystiq page is focused. */
    shortcut: Shortcut,
  }),
  readAloud: z.object({
    /** Voice of the speech service; null means its default voice. */
    voice: z.string().min(1).max(100).nullable(),
    /** Playback speed, 0.5 to 2. */
    speed: z.number().min(0.5).max(2),
  }),
});
export type Preferences = z.infer<typeof Preferences>;
export const DEFAULT_PREFERENCES: Preferences = {
  languages: [],
  dictation: { shortcut: 'Ctrl+Shift+Space' },
  readAloud: { voice: null, speed: 1 },
};

/** A voice the speech service offers for reading aloud. */
export const SpeechVoice = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  previewUrl: z.string().nullable(),
  /** Languages the voice is verified for, when known (ISO 639-1). */
  languages: z.array(z.string()),
});
export type SpeechVoice = z.infer<typeof SpeechVoice>;

/**
 * Text to read aloud, in parts that play one after another. With a speech
 * service each part has an audio link (MP3, made on first play and kept with
 * the item); with provider "browser" the device's own voice reads the text.
 */
export const ReadAloud = z.object({
  itemId: Id,
  /** The item whose text is read: itself, or a file's transcript or extracted text. */
  textItemId: Id,
  revisionId: Id,
  provider: z.string(),
  voice: z.string().nullable(),
  totalChars: z.number().int(),
  parts: z.array(
    z.object({
      start: z.number().int(),
      end: z.number().int(),
      text: z.string(),
      url: z.string().nullable(),
      expiresAt: Timestamp.nullable(),
    }),
  ),
});
export type ReadAloud = z.infer<typeof ReadAloud>;

/** Words dictated into a Mystiq field. The audio is not kept. */
export const Dictation = z.object({
  text: z.string(),
  languages: z.array(z.string()),
  durationMs: z.number().int(),
});
export type Dictation = z.infer<typeof Dictation>;

/** One version of an item's content, newest first in listings. */
export const RevisionSummary = z.object({
  revisionId: Id,
  /** 1 for the first version. */
  number: z.number().int(),
  reason: z.string(),
  createdBy: ActorRef,
  createdAt: Timestamp,
  current: z.boolean(),
});
export type RevisionSummary = z.infer<typeof RevisionSummary>;

/**
 * A Site version: its HTML package (self-contained: inline CSS and JavaScript,
 * nothing loaded from elsewhere) and a short-lived link that shows it in a
 * sandboxed frame (a Site page under /site on Mystiq's own address).
 */
export const SiteVersion = z.object({
  itemId: Id,
  revisionId: Id,
  number: z.number().int(),
  current: z.boolean(),
  /** The page as stored (up to maxChars; `truncated` says when more exists). */
  html: z.string(),
  truncated: z.boolean(),
  /** How the version was made: written by AI from sources, or imported from an HTML file. */
  made: z.enum(['generated', 'imported']),
  instructions: z.string().nullable(),
  previewUrl: z.string(),
  expiresAt: Timestamp,
});
export type SiteVersion = z.infer<typeof SiteVersion>;

/** Where a comment points: the whole version, a text range, a time in a recording, or an element of a Site. */
export const Anchor = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('item') }),
  z.object({
    kind: z.literal('text'),
    start: z.number().int().min(0),
    end: z.number().int().min(0),
    /** The commented words, and a little text either side, as they were in that revision. */
    quote: z.string(),
    prefix: z.string(),
    suffix: z.string(),
  }),
  z.object({ kind: z.literal('time'), startMs: z.number().int().min(0), endMs: z.number().int().min(0) }),
  z.object({
    kind: z.literal('element'),
    /** Position of the element in the Site's page (a path of tag names and indexes). */
    path: z.string(),
    /** The selected words, as shown in that version. */
    quote: z.string(),
  }),
]);
export type Anchor = z.infer<typeof Anchor>;

/**
 * A comment on one exact revision of an item. It stays with that revision: on
 * a later version it is shown as belonging to an earlier one, and a text
 * comment is found again only where its exact words still are.
 */
export const Annotation = z.object({
  id: Id,
  itemId: Id,
  revisionId: Id,
  anchor: Anchor,
  body: z.string(),
  status: z.enum(['open', 'resolved']),
  author: ActorRef,
  createdAt: Timestamp,
  updatedAt: Timestamp,
  resolvedAt: Timestamp.nullable(),
  /** Made on the item's current revision. */
  onCurrentRevision: z.boolean(),
  /** For a text comment: where its words are in the current text, or null when they are gone (detached). */
  located: z.object({ start: z.number().int(), end: z.number().int() }).nullable(),
});
export type Annotation = z.infer<typeof Annotation>;

export const Invitation = z.object({
  id: Id,
  email: z.string(),
  role: Role,
  status: z.enum(['pending', 'accepted', 'revoked']),
  /** Pending invitations past this time can no longer be accepted. */
  expiresAt: Timestamp,
  invitedBy: z.string().nullable(),
  createdAt: Timestamp,
});
export type Invitation = z.infer<typeof Invitation>;

/** Returned once, when the invitation is created: the link holds the secret. */
export const InvitationLink = Invitation.extend({ url: z.string() });
export type InvitationLink = z.infer<typeof InvitationLink>;

export const InvitationPreview = z.object({
  workspaceId: Id,
  workspaceName: z.string(),
  role: Role,
  email: z.string(),
  invitedBy: z.string().nullable(),
  status: z.enum(['pending', 'accepted', 'revoked', 'expired']),
  expiresAt: Timestamp,
  /** Whether the signed-in person can accept it (their email matches). */
  forYou: z.boolean(),
});
export type InvitationPreview = z.infer<typeof InvitationPreview>;

export const Share = z.object({
  id: Id,
  itemId: Id,
  /** Public read-only link; anyone with it can view the shared version. */
  url: z.string(),
  revisionId: Id,
  title: z.string(),
  status: z.enum(['active', 'revoked', 'expired']),
  /** False when the item changed after this version was shared. */
  latest: z.boolean(),
  expiresAt: Timestamp.nullable(),
  viewCount: z.number().int(),
  lastViewedAt: Timestamp.nullable(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type Share = z.infer<typeof Share>;

export const CommandOutcome = z.object({
  action: z.string(),
  itemId: Id.nullable(),
  status: z.enum(['done', 'failed']),
  message: z.string(),
  /** For a written note: the background operation that writes it. */
  operationId: Id.nullable(),
});
export type CommandOutcome = z.infer<typeof CommandOutcome>;

export const CommandResult = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('clarify'), question: z.string() }),
  z.object({
    kind: z.literal('done'),
    summary: z.string(),
    outcomes: z.array(CommandOutcome),
    /** Send back to undo; each step applies only if its item is unchanged since. */
    undo: z.object({ steps: z.array(z.any()), cancelOperationIds: z.array(Id) }),
    /** For "rewrite this": the replacement for the selected text, applied by the editor (so normal undo works). */
    replacement: z.string().nullable(),
  }),
]);
export type CommandResult = z.infer<typeof CommandResult>;

export const UndoOutcome = z.object({ itemId: Id.nullable(), status: z.enum(['undone', 'skipped', 'failed']), message: z.string() });
export type UndoOutcome = z.infer<typeof UndoOutcome>;
